<script lang="ts">
  import {
    Pacientes, Notas, Signos, FarmaciaClinica, Episodios, downloadAuthenticatedFile,
    type Paciente, type Nota, type Signo, type Receta, type RecetaItem,
    type BorradorConsultaIa,
  } from '../lib/api';
  import ConsultaIaPanel from './ConsultaIaPanel.svelte';
  import { fichaDesdeMotivo, motivoDesdeFicha } from '../lib/motivo-sync';
  import { session } from '../lib/auth.svelte';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { resolveMedicoId, resolveMedicoProfesional } from '../lib/medico-session';
  import { clinicPreview, effectiveOrgTipo } from '../lib/clinic-preview.svelte';
  import { isPilotMode } from '../lib/pilot';
  import { isDemoTenant } from '../lib/clinic-preview.svelte';
  import {
    EXPEDIENTE_CUI_STORAGE,
    demoCuiPrincipal,
    demoExpedienteHints,
    DEMO_CUIS,
    FISIO_DEMO_EVAL_TAB_KEY,
  } from '../lib/clinic-demo-seed';
  import {
    autorFisioterapista,
    isClinicaFisioterapia,
    NOTA_FISIO_PLACEHOLDER,
    SIGNOS_FISIO_LABELS,
  } from '../lib/fisioterapia';
  import {
    EJERCICIO_VACIO,
    PLANTILLAS_EJERCICIO_FISIO,
    RUTINA_FISIO_TIPO,
    formatRutinaWhatsApp,
    notasEvolucionClinica,
    rutinaMasReciente,
    rutinaVacia,
    serializeRutina,
    whatsappEnlaceRutina,
    type RutinaFisio,
  } from '../lib/fisioterapia-rutina';
  import { evaluacionesDesdeNotas } from '../lib/fisioterapia-evaluaciones';
  import { edadDesdeNacimiento } from '../lib/csv-import';
  import FisioterapiaEvaluacionesPanel from './FisioterapiaEvaluacionesPanel.svelte';
  import OdontogramaPanel from './OdontogramaPanel.svelte';
  import PacientePicker from './PacientePicker.svelte';
  import {
    listPlacas, addPlaca, removePlaca,
    type PlacaDental, type TipoPlacaDental,
  } from '../lib/expediente-rx';

  const orgTipo = $derived(
    effectiveOrgTipo(
      tenantCtx.orgTipo as import('../lib/navigation').OrgTipo | null,
      tenantCtx.isHospitalEdition,
      session.value?.tenantId,
    ) ?? clinicPreview.org,
  );
  const esDental = $derived(orgTipo === 'clinica-odontologica');
  const esFisio = $derived(isClinicaFisioterapia(orgTipo));
  const esHospital = $derived(orgTipo === 'hospital-privado');
  const esDemo = $derived(isPilotMode() || isDemoTenant(session.value?.tenantId));
  const demoHint = $derived(
    esDemo ? demoExpedienteHints(clinicPreview.org ?? tenantCtx.orgTipo) : null,
  );

  const CUI_STORAGE = EXPEDIENTE_CUI_STORAGE;

  let cuiInput = $state('');
  let pacientePickerId = $state('');
  let paciente = $state<Paciente | null>(null);
  let buscando = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let subTab = $state<'resumen' | 'ficha' | 'evolucion' | 'signos' | 'rutina' | 'odontograma' | 'placas' | 'receta'>('resumen');

  let notas = $state<Nota[]>([]);
  let notasClinicas = $derived(notasEvolucionClinica(notas));
  let evaluacionesFisio = $derived(evaluacionesDesdeNotas(notas));
  let rutinaEdit = $state<RutinaFisio>(rutinaVacia());
  let rutinaVersiones = $derived(notas.filter(n => n.tipo === RUTINA_FISIO_TIPO).length);
  let signos = $state<Signo[]>([]);
  let placas = $state<PlacaDental[]>([]);
  let cargandoDetalle = $state(false);
  let episodioAbiertoId = $state<string | null>(null);

  let mostrarAlta = $state(false);
  let nuevoPac = $state<Paciente>({
    nombres: '', apellidos: '', cui: '', sexo: 'M', telefono: '', fechaNacimiento: '',
  });

  let notaNueva = $state({ autor: '', contenido: '' });
  const SpeechRecognition = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
    : null;
  let dictandoCampo = $state<string | null>(null);

  function toggleDictadoFicha(campo: keyof typeof fichaEdit) {
    if (!SpeechRecognition) { error = 'El dictado por voz no está disponible en este navegador.'; return; }
    if (dictandoCampo === campo) { dictandoCampo = null; return; }
    const rec = new SpeechRecognition();
    rec.lang = 'es-GT'; rec.interimResults = false;
    rec.onresult = (ev: any) => {
      const txt = ev.results[0][0].transcript;
      const prev = fichaEdit[campo] ?? '';
      fichaEdit = { ...fichaEdit, [campo]: (prev ? prev + ' ' : '') + txt };
      dictandoCampo = null;
    };
    rec.onerror = () => { dictandoCampo = null; };
    rec.onend = () => { dictandoCampo = null; };
    dictandoCampo = campo;
    rec.start();
  }

  let signoNuevo = $state<Signo>({
    pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null,
  });

  let fichaEdit = $state({
    antecedentesPersonales: '', antecedentesFamiliares: '', habitosToxicos: '',
    alergias: '', antecedentesQuirurgicos: '',
    motivoConsulta: '', historiaEnfermedadActual: '', diagnostico: '',
  });
  let guardandoFicha = $state(false);

  function cargarFicha(p: Paciente, episodioAbierto?: { motivo?: string | null }) {
    const desdeEp = episodioAbierto?.motivo?.trim() ? fichaDesdeMotivo(episodioAbierto.motivo) : null;
    fichaEdit = {
      antecedentesPersonales: p.antecedentesPersonales ?? '',
      antecedentesFamiliares: p.antecedentesFamiliares ?? '',
      habitosToxicos: p.habitosToxicos ?? '',
      alergias: p.alergias ?? '',
      antecedentesQuirurgicos: p.antecedentesQuirurgicos ?? '',
      motivoConsulta: p.motivoConsulta ?? desdeEp?.motivoConsulta ?? '',
      historiaEnfermedadActual: p.historiaEnfermedadActual ?? desdeEp?.historiaEnfermedadActual ?? '',
      diagnostico: p.diagnostico ?? '',
    };
  }

  /** Solo rellena campos con texto del borrador; no guarda (sigue el botón Guardar). */
  function aplicarBorradorIa(b: BorradorConsultaIa) {
    const pick = (v?: string) => (v ?? '').trim();
    fichaEdit = {
      antecedentesPersonales: pick(b.antecedentesPersonales) || fichaEdit.antecedentesPersonales,
      antecedentesFamiliares: pick(b.antecedentesFamiliares) || fichaEdit.antecedentesFamiliares,
      habitosToxicos: pick(b.habitosToxicos) || fichaEdit.habitosToxicos,
      alergias: pick(b.alergias) || fichaEdit.alergias,
      antecedentesQuirurgicos: pick(b.antecedentesQuirurgicos) || fichaEdit.antecedentesQuirurgicos,
      motivoConsulta: pick(b.motivoConsulta) || fichaEdit.motivoConsulta,
      historiaEnfermedadActual: pick(b.historiaEnfermedadActual) || fichaEdit.historiaEnfermedadActual,
      diagnostico: pick(b.diagnostico) || fichaEdit.diagnostico,
    };
    if (pick(b.diagnostico) && !recetaDiagnostico.trim()) {
      recetaDiagnostico = pick(b.diagnostico);
    }
    info = 'Borrador IA aplicado a la ficha. Revise y pulse «Guardar ficha clínica» para persistir.';
  }

  async function guardarFicha(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    guardandoFicha = true;
    error = null;
    try {
      const p = await Pacientes.actualizar(paciente.id, { ...paciente, ...fichaEdit });
      paciente = p;
      const motivoEp = motivoDesdeFicha(fichaEdit);
      if (episodioAbiertoId && motivoEp) {
        await Episodios.actualizar(episodioAbiertoId, { motivo: motivoEp });
      }
      if (!recetaDiagnostico.trim()) recetaDiagnostico = fichaEdit.diagnostico ?? '';
      info = 'Ficha clínica guardada.';
      await cargarExpediente(p);
    } catch (err: any) {
      error = err?.message ?? String(err);
    } finally {
      guardandoFicha = false;
    }
  }

  const TIPOS_PLACA: { value: TipoPlacaDental; label: string }[] = [
    { value: 'PERIAPICAL', label: 'Periapical' },
    { value: 'BITEWING', label: 'Bitewing' },
    { value: 'PANORAMICA', label: 'Panorámica' },
    { value: 'CBCT', label: 'Tomografía (CBCT)' },
    { value: 'OTRO', label: 'Otra' },
  ];

  let placaNueva = $state({
    tipo: 'PERIAPICAL' as TipoPlacaDental,
    dientes: '',
    nota: '',
    medico: '',
    archivoNombre: '',
    imagenUrl: '',
  });

  let recetasPaciente = $state<Receta[]>([]);
  let recetaDiagnostico = $state('');
  let recetaItems = $state<RecetaItem[]>([]);
  let medicoActual = $state('');
  const fisioterapistaActual = $derived(
    autorFisioterapista({ profesional: medicoActual, usuario: session.value?.usuario }),
  );

  function normalizarCui(raw: string): string {
    return raw.replace(/\D/g, '');
  }

  function cuiValido(cui: string): boolean {
    return cui.length === 13;
  }

  async function cargarExpediente(p: Paciente) {
    if (!p.id) return;
    cargandoDetalle = true;
    error = null;
    try {
      const [n, s, rec, prof, eps] = await Promise.all([
        Notas.porPaciente(p.id).catch(() => []),
        Signos.porPaciente(p.id).catch(() => []),
        FarmaciaClinica.recetas(p.id).catch(() => []),
        resolveMedicoProfesional().catch(() => null),
        Episodios.porPaciente(p.id).catch(() => []),
      ]);
      notas = n;
      rutinaEdit = rutinaMasReciente(n) ?? rutinaVacia();
      signos = s;
      recetasPaciente = rec;
      medicoActual = prof?.nombre ?? session.value?.usuario ?? '';
      placas = listPlacas(p.id);
      const abierto = (eps as any[]).find((ep: any) => ep.estado === 'ABIERTO');
      episodioAbiertoId = abierto?.id ?? null;
      cargarFicha(p, abierto);
      if (!recetaDiagnostico.trim() && (p.diagnostico ?? '').trim()) {
        recetaDiagnostico = p.diagnostico ?? '';
      }
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      cargandoDetalle = false;
    }
  }

  async function buscarPorCui(e?: SubmitEvent) {
    e?.preventDefault();
    error = null;
    info = null;
    mostrarAlta = false;
    const cui = normalizarCui(cuiInput);
    if (!cuiValido(cui)) {
      error = 'El CUI debe tener 13 dígitos (módulo 11).';
      paciente = null;
      return;
    }
    buscando = true;
    try {
      const p = await Pacientes.porCui(cui);
      paciente = p;
      pacientePickerId = p.id ?? '';
      cuiInput = cui;
      sessionStorage.setItem(CUI_STORAGE, cui);
      info = `Expediente cargado · ${p.nombres} ${p.apellidos}`;
      await cargarExpediente(p);
    } catch {
      paciente = null;
      mostrarAlta = true;
      nuevoPac = { ...nuevoPac, cui };
      info = 'No hay expediente con ese CUI. Complete los datos para crearlo.';
    } finally {
      buscando = false;
    }
  }

  async function onPickPaciente(id: string, p?: Paciente) {
    error = null;
    info = null;
    mostrarAlta = false;
    if (!id || !p) {
      pacientePickerId = '';
      return;
    }
    paciente = p;
    pacientePickerId = id;
    const cui = normalizarCui(p.cui ?? '');
    if (cui) {
      cuiInput = cui;
      sessionStorage.setItem(CUI_STORAGE, cui);
    }
    info = `Expediente cargado · ${p.nombres} ${p.apellidos}`;
    await cargarExpediente(p);
  }

  async function crearExpediente(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    const cui = normalizarCui(nuevoPac.cui ?? cuiInput);
    if (!cuiValido(cui)) { error = 'CUI inválido.'; return; }
    if (!nuevoPac.nombres.trim() || !nuevoPac.apellidos.trim()) {
      error = 'Nombres y apellidos son obligatorios.';
      return;
    }
    try {
      const p = await Pacientes.crear({ ...nuevoPac, cui });
      paciente = p;
      pacientePickerId = p.id ?? '';
      mostrarAlta = false;
      cuiInput = cui;
      sessionStorage.setItem(CUI_STORAGE, cui);
      info = `Expediente creado para CUI ${cui}.`;
      await cargarExpediente(p);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  async function guardarRutina(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    const ejercicios = rutinaEdit.ejercicios.filter(x => x.nombre.trim());
    if (ejercicios.length === 0) {
      error = 'Agregue al menos un ejercicio con nombre.';
      return;
    }
    error = null;
    try {
      await Notas.crear({
        pacienteId: paciente.id,
        autor: fisioterapistaActual,
        tipo: RUTINA_FISIO_TIPO,
        contenido: serializeRutina({ ...rutinaEdit, ejercicios }),
      });
      info = 'Rutina domiciliaria guardada en el expediente.';
      await cargarExpediente(paciente);
      subTab = 'rutina';
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  function enviarRutinaWhatsApp() {
    if (!paciente) return;
    const nombre = `${paciente.nombres} ${paciente.apellidos}`.trim();
    const msg = formatRutinaWhatsApp(rutinaEdit, nombre, {
      clinicaNombre: tenantCtx.displayName,
      fisioterapista: fisioterapistaActual,
    });
    const url = whatsappEnlaceRutina(paciente.telefono, msg);
    if (!url) {
      error = 'El paciente no tiene teléfono/WhatsApp registrado.';
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    info = 'Se abrió WhatsApp con la rutina lista para enviar.';
    error = null;
  }

  function agregarEjercicioRutina() {
    rutinaEdit = { ...rutinaEdit, ejercicios: [...rutinaEdit.ejercicios, EJERCICIO_VACIO()] };
  }

  function quitarEjercicioRutina(idx: number) {
    rutinaEdit = {
      ...rutinaEdit,
      ejercicios: rutinaEdit.ejercicios.filter((_, i) => i !== idx),
    };
  }

  function agregarPlantillaRutina(p: (typeof PLANTILLAS_EJERCICIO_FISIO)[number]) {
    rutinaEdit = { ...rutinaEdit, ejercicios: [...rutinaEdit.ejercicios, { ...p }] };
  }

  async function agregarNota(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id || !notaNueva.contenido.trim()) return;
    error = null;
    try {
      await Notas.crear({
        pacienteId: paciente.id,
        autor: notaNueva.autor || fisioterapistaActual,
        contenido: notaNueva.contenido,
      });
      notaNueva = { autor: '', contenido: '' };
      await cargarExpediente(paciente);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  async function registrarSigno(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    error = null;
    try {
      await Signos.registrar({
        pacienteId: paciente.id,
        ta: signoNuevo.ta || null,
        fc: signoNuevo.fc != null ? Number(signoNuevo.fc) : null,
        fr: signoNuevo.fr != null ? Number(signoNuevo.fr) : null,
        temp: signoNuevo.temp != null ? Number(signoNuevo.temp) : null,
        satO2: signoNuevo.satO2 != null ? Number(signoNuevo.satO2) : null,
        peso: signoNuevo.peso != null ? Number(signoNuevo.peso) : null,
        talla: signoNuevo.talla != null ? Number(signoNuevo.talla) : null,
      });
      signoNuevo = { pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null };
      await cargarExpediente(paciente);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  function onArchivoPlaca(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    placaNueva.archivoNombre = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      placaNueva.imagenUrl = typeof reader.result === 'string' ? reader.result : '';
    };
    reader.readAsDataURL(file);
  }

  function guardarPlaca(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    if (!placaNueva.dientes.trim()) { error = 'Indique el diente o región.'; return; }
    addPlaca({
      pacienteId: paciente.id,
      cui: paciente.cui,
      fecha: new Date().toISOString().slice(0, 10),
      tipo: placaNueva.tipo,
      dientes: placaNueva.dientes,
      nota: placaNueva.nota,
      medico: placaNueva.medico || session.value?.usuario || 'Odontólogo',
      archivoNombre: placaNueva.archivoNombre || undefined,
      imagenUrl: placaNueva.imagenUrl || undefined,
    });
    placaNueva = { tipo: 'PERIAPICAL', dientes: '', nota: '', medico: '', archivoNombre: '', imagenUrl: '' };
    placas = listPlacas(paciente.id);
    info = 'Placa registrada en el expediente clínico.';
    error = null;
  }

  function eliminarPlaca(id: string) {
    if (!paciente?.id) return;
    removePlaca(id);
    placas = listPlacas(paciente.id);
  }

  function abrirDemoCui(cui: string) {
    cuiInput = cui;
    void buscarPorCui();
  }

  function agregarItemReceta() {
    recetaItems.push({ descripcion: '', cantidad: 1, indicaciones: '' });
  }
  function quitarItemReceta(i: number) {
    recetaItems.splice(i, 1);
  }

  async function emitirReceta(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    if (recetaItems.length === 0) { error = 'Agrega al menos un medicamento.'; return; }
    error = null;
    try {
      const medicoId = await resolveMedicoId();
      await FarmaciaClinica.crearReceta({
        pacienteId: paciente.id,
        medicoId: medicoId ?? null,
        diagnostico: recetaDiagnostico || null,
        items: recetaItems.map(it => ({ ...it, cantidad: Number(it.cantidad) })),
      });
      recetaDiagnostico = '';
      recetaItems = [];
      info = 'Receta emitida. La farmacia puede cargarla en el POS o imprimirla aquí.';
      await cargarExpediente(paciente);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  function abrirHtmlReceta(html: string) {
    const w = window.open('', '_blank');
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  }

  async function imprimirReceta(r: Receta) {
    if (!r.id || !paciente) return;
    error = null;
    const nombre = `${paciente.nombres} ${paciente.apellidos}`.trim();
    try {
      await downloadAuthenticatedFile(
        FarmaciaClinica.recetaPdf(r.id, nombre, paciente.cui ?? ''),
        `receta-${r.id}.pdf`,
      );
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  async function verReceta(r: Receta) {
    if (!r.id || !paciente) return;
    error = null;
    const nombre = `${paciente.nombres} ${paciente.apellidos}`.trim();
    try {
      abrirHtmlReceta(await FarmaciaClinica.recetaImprimir(r.id, nombre, paciente.cui ?? ''));
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  function esc(v: any): string {
    return String(v ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function imprimirExpediente() {
    if (!paciente) return;
    const nombre = `${paciente.nombres} ${paciente.apellidos}`.trim();
    const ahora = new Date();
    const fecha = ahora.toLocaleString('es-GT');
    const clinica = tenantCtx.displayName ?? 'Toscana';
    const usuario = session.value?.usuario ?? '';
    const cui = paciente.cui ?? normalizarCui(cuiInput);

    let edad = '—';
    if (paciente.fechaNacimiento) {
      const nac = new Date(paciente.fechaNacimiento);
      const diff = ahora.getTime() - nac.getTime();
      edad = `${Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25))} años`;
    }

    const filasSignos = signos.map(s => `<tr>
      <td>${esc(s.registradoEn ? new Date(s.registradoEn).toLocaleString('es-GT') : '—')}</td>
      <td>${esc(s.ta)}</td><td>${esc(s.fc)}</td><td>${esc(s.fr)}</td>
      <td>${esc(s.temp)}</td><td>${esc(s.satO2)}</td><td>${esc(s.peso)}</td><td>${esc(s.talla)}</td>
    </tr>`).join('');

    const filasRecetas = recetasPaciente.map(r => {
      const medicamentos = (r.items ?? []).map(it => {
        const ind = it.indicaciones ? ` — ${it.indicaciones}` : '';
        return `${it.descripcion} (${it.cantidad})${ind}`;
      }).join('; ');
      return `<tr>
        <td>${esc(r.fecha?.slice(0, 10))}</td>
        <td>${esc(r.diagnostico)}</td>
        <td>${esc(medicamentos)}</td>
        <td>${esc(r.estado)}</td>
      </tr>`;
    }).join('');

    const notasHtml = notasClinicas.map(n => `<div class="nota">
      <div class="nota-meta">${esc(n.autor)} · ${esc(n.registradaEn ? new Date(n.registradaEn).toLocaleString('es-GT') : '')}</div>
      <p>${esc(n.contenido)}</p>
    </div>`).join('');

    const filasPlacas = placas.map(p => `<tr>
      <td>${esc(p.dientes)}</td><td>${esc(p.tipo)}</td><td>${esc(p.fecha)}</td>
      <td>${esc(p.medico)}</td><td>${esc(p.nota)}</td>
    </tr>`).join('');

    const seccion = (titulo: string, cuerpo: string) =>
      `<h2>${titulo}</h2>${cuerpo || '<p class="vacio">Sin registro.</p>'}`;

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8">
<title>Expediente ${esc(cui)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111; margin: 28px; font-size: 13px; }
  .membrete { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #15803d; padding-bottom: 10px; margin-bottom: 4px; }
  .membrete .logo { font-weight: 800; color: #15803d; font-size: 16px; }
  .membrete .lugar { color: #555; font-size: 11px; }
  .membrete .folio { color: #666; font-size: 11px; text-align: right; }
  h1 { font-size: 20px; text-align: center; letter-spacing: 1px; margin: 10px 0 2px; color: #111; }
  .sub { text-align: center; color: #555; margin: 0 0 16px; }
  .dato { display: grid; grid-template-columns: 150px 1fr 150px 1fr; gap: 3px 12px; margin: 0 0 14px; font-size: 12px; border: 1px solid #d1d5db; padding: 10px 12px; }
  .dato b { color: #333; font-weight: 600; }
  h2 { font-size: 13px; color: #166534; border-bottom: 1px solid #16a34a; padding-bottom: 4px; margin: 20px 0 8px; text-transform: uppercase; letter-spacing: .04em; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0; }
  th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; vertical-align: top; font-size: 12px; }
  th { background: #f0fdf4; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; color: #555; }
  .nota { border-left: 4px solid #16a34a; padding: 6px 10px; margin: 8px 0; background: #fafafa; }
  .nota-meta { font-size: 11px; color: #666; margin-bottom: 4px; }
  .nota p { margin: 0; white-space: pre-wrap; }
  .texto { border: 1px solid #e5e7eb; padding: 8px 10px; min-height: 20px; white-space: pre-wrap; font-size: 12px; }
  .vacio { color: #999; font-style: italic; }
  .firma { margin-top: 40px; display: flex; justify-content: space-between; gap: 40px; }
  .firma .bloque { flex: 1; text-align: center; font-size: 12px; color: #333; }
  .firma .linea { border-top: 1px solid #333; padding-top: 6px; margin-top: 50px; }
  .footer { margin-top: 26px; font-size: 10px; color: #888; border-top: 1px solid #d1d5db; padding-top: 8px; text-align: center; }
  @media print { body { margin: 12mm; } h2 { break-after: avoid; } .nota, table, .dato { break-inside: avoid; } }
</style></head>
<body onload="setTimeout(function(){window.print()},300)">
  <div class="membrete">
    <div><div class="logo">${esc(clinica)}</div><div class="lugar">Clínicas privadas</div></div>
    <div class="folio">Expediente ${esc(cui)}<br>${esc(fecha)}</div>
  </div>
  <h1>EXPEDIENTE CLÍNICO</h1>
  <div class="sub">${esc(nombre)}</div>

  ${seccion('1. Identificación del paciente', `<div class="dato">
    <b>Nombre</b><span>${esc(nombre)}</span>
    <b>CUI</b><span>${esc(cui)}</span>
    <b>Sexo</b><span>${esc(paciente.sexo)}</span>
    <b>Edad</b><span>${esc(edad)}</span>
    <b>Fecha de nacimiento</b><span>${esc(paciente.fechaNacimiento)}</span>
    <b>Teléfono</b><span>${esc(paciente.telefono)}</span>
    <b>Dirección</b><span>${esc(paciente.direccion)}</span>
    <b>Correo</b><span>${esc(paciente.email)}</span>
  </div>`)}

  ${seccion('2. Ficha clínica', `<div class="dato">
    <b>Motivo</b><span>${esc(paciente.motivoConsulta || fichaEdit.motivoConsulta)}</span>
    <b>Diagnóstico</b><span>${esc(paciente.diagnostico || fichaEdit.diagnostico)}</span>
    <b>Alergias</b><span>${esc(paciente.alergias || fichaEdit.alergias)}</span>
    <b>APP</b><span>${esc(paciente.antecedentesPersonales || fichaEdit.antecedentesPersonales)}</span>
    <b>APF</b><span>${esc(paciente.antecedentesFamiliares || fichaEdit.antecedentesFamiliares)}</span>
    <b>AQX</b><span>${esc(paciente.antecedentesQuirurgicos || fichaEdit.antecedentesQuirurgicos)}</span>
    <b>Hábitos</b><span>${esc(paciente.habitosToxicos || fichaEdit.habitosToxicos)}</span>
    <b>HEA</b><span>${esc(paciente.historiaEnfermedadActual || fichaEdit.historiaEnfermedadActual)}</span>
  </div>`)}

  ${seccion('3. Signos vitales', signos.length
    ? `<table><thead><tr><th>Fecha</th><th>TA</th><th>FC</th><th>FR</th><th>Temp</th><th>Sat</th><th>Peso</th><th>Talla</th></tr></thead><tbody>${filasSignos}</tbody></table>`
    : '')}

  ${placas.length ? seccion('4. Exploraciones complementarias', `<table><thead><tr><th>Diente(s)</th><th>Tipo</th><th>Fecha</th><th>Odontólogo</th><th>Nota</th></tr></thead><tbody>${filasPlacas}</tbody></table>`) : ''}

  ${seccion('5. Tratamiento / recetas', recetasPaciente.length
    ? `<table><thead><tr><th>Fecha</th><th>Diagnóstico</th><th>Medicamentos</th><th>Estado</th></tr></thead><tbody>${filasRecetas}</tbody></table>`
    : '')}

  ${seccion('6. Notas de evolución', notasHtml)}

  <div class="firma">
    <div class="bloque"><div class="linea">Firma y sello del médico</div></div>
    <div class="bloque"><div class="linea">${esc(usuario)}</div></div>
  </div>

  <div class="footer">Documento confidencial · Generado el ${esc(fecha)} por ${esc(usuario)}</div>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    } else {
      error = 'El navegador bloqueó la ventana de impresión.';
    }
  }

  let autoBusquedaKey = $state('');

  $effect(() => {
    if (!esFisio || !esDemo || !paciente?.id) return;
    try {
      if (sessionStorage.getItem(FISIO_DEMO_EVAL_TAB_KEY)) {
        subTab = 'signos';
        sessionStorage.removeItem(FISIO_DEMO_EVAL_TAB_KEY);
      }
    } catch { /* ignore */ }
  });
</script>

<div class="page">
  <div class="page-header">
    <div class="header-icon">📋</div>
    <div class="header-info">
      <h1>Expediente clínico</h1>
      <p>Ficha única por CUI · evolución, signos, recetas, placas</p>
    </div>
  </div>

  {#if esDemo && demoHint}
    <div class="demo-banner" role="note">
      <strong>Demo expediente clínico</strong>
      <span>
        Busque CUI
        <button type="button" class="cui-link" onclick={() => abrirDemoCui(demoHint.cui)}>
          {demoHint.cui}
        </button>
        · {demoHint.nombre}
      </span>
      <span class="demo-more">
        {#if orgTipo === 'clinica-medica'}
          También: {DEMO_CUIS.secundario} (Sofía Martínez)
        {:else if orgTipo === 'clinica-odontologica'}
          CUI dental: {DEMO_CUIS.dental} · placas en pestaña RX
        {:else if esFisio && esDemo}
          Menú <strong>Evaluación funcional</strong> o pestaña <strong>Evaluaciones</strong> aquí:
          dolor · edema · sensibilidad · fuerza · reflejos · ROM — plantillas editables en «Personalizar»
        {/if}
      </span>
    </div>
  {/if}

  <div class="cui-bar">
    <label class="cui-field picker-field">
      <span>Buscar por nombre</span>
      <PacientePicker
        bind:value={pacientePickerId}
        onPick={onPickPaciente}
        placeholder="Nombre, apellido o CUI…"
      />
    </label>
    <form class="cui-form" onsubmit={buscarPorCui}>
      <label class="cui-field">
        <span>CUI del paciente</span>
        <input
          inputmode="numeric"
          maxlength="17"
          placeholder="Ej. 1234 56789 0101"
          bind:value={cuiInput}
          disabled={buscando}
        />
      </label>
      <button type="submit" disabled={buscando}>{buscando ? 'Buscando…' : 'Abrir expediente'}</button>
    </form>
  </div>

  {#if error}<div class="msg msg-error">{error}</div>{/if}
  {#if info}<div class="msg msg-ok">{info}</div>{/if}

  {#if mostrarAlta}
    <div class="alta-card">
      <h3>Crear expediente clínico</h3>
      <p class="hint">No existe registro con CUI <strong>{normalizarCui(cuiInput)}</strong>. Complete los datos mínimos.</p>
      <form class="form-grid" onsubmit={crearExpediente}>
        <label>Nombres <input bind:value={nuevoPac.nombres} required /></label>
        <label>Apellidos <input bind:value={nuevoPac.apellidos} required /></label>
        <label>CUI <input value={normalizarCui(nuevoPac.cui ?? cuiInput)} readonly class="readonly" /></label>
        <label>Sexo
          <select bind:value={nuevoPac.sexo}>
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
            <option value="O">Otro</option>
          </select>
        </label>
        <label>Fecha nacimiento <input type="date" bind:value={nuevoPac.fechaNacimiento} /></label>
        <label>Teléfono <input bind:value={nuevoPac.telefono} /></label>
        <button type="submit" class="primary">Crear expediente</button>
      </form>
    </div>
  {/if}

  {#if paciente}
    <div class="paciente-card">
      <div class="paciente-head">
        <div>
          <strong>{paciente.nombres} {paciente.apellidos}</strong>
          <span class="cui-badge">CUI {paciente.cui ?? normalizarCui(cuiInput)}</span>
        </div>
        <div class="head-actions">
          <span class="meta">Edad {edadDesdeNacimiento(paciente.fechaNacimiento)}</span>
          {#if paciente.telefono}<span class="meta">{paciente.telefono}</span>{/if}
          <button type="button" class="print-btn" onclick={imprimirExpediente}>Imprimir expediente</button>
        </div>
      </div>

      <nav class="subtabs">
        <button type="button" class:active={subTab === 'resumen'} onclick={() => subTab = 'resumen'}>Resumen</button>
        <button type="button" class:active={subTab === 'ficha'} onclick={() => subTab = 'ficha'}>Ficha clínica</button>
        <button type="button" class:active={subTab === 'evolucion'} onclick={() => subTab = 'evolucion'}>Evolución</button>
        <button type="button" class:active={subTab === 'signos'} onclick={() => subTab = 'signos'}>
          {esFisio ? `Evaluaciones (${evaluacionesFisio.length})` : 'Signos'}
        </button>
        {#if esFisio}
          <button type="button" class:active={subTab === 'rutina'} onclick={() => subTab = 'rutina'}>
            Rutina domiciliaria ({rutinaEdit.ejercicios.filter(e => e.nombre.trim()).length})
          </button>
        {/if}
        {#if !esFisio}
          <button type="button" class:active={subTab === 'receta'} onclick={() => subTab = 'receta'}>
            Receta ℞ ({recetasPaciente.length})
          </button>
        {/if}
        {#if esDental}
          <button type="button" class:active={subTab === 'odontograma'} onclick={() => subTab = 'odontograma'}>
            Odontograma (32)
          </button>
          <button type="button" class:active={subTab === 'placas'} onclick={() => subTab = 'placas'}>
            Placas / RX ({placas.length})
          </button>
        {/if}
      </nav>

      {#if cargandoDetalle}
        <p class="hint">Cargando expediente…</p>
      {:else if subTab === 'resumen'}
        <div class="resumen-grid">
          <div class="stat"><span>Notas clínicas</span><strong>{notasClinicas.length}</strong></div>
          <div class="stat"><span>{esFisio ? 'Evaluaciones' : 'Signos vitales'}</span><strong>{esFisio ? evaluacionesFisio.length : signos.length}</strong></div>
          {#if esFisio}
            <div class="stat"><span>Ejercicios en rutina</span><strong>{rutinaEdit.ejercicios.filter(e => e.nombre.trim()).length}</strong></div>
          {:else}
            <div class="stat"><span>Recetas</span><strong>{recetasPaciente.length}</strong></div>
          {/if}
          {#if esDental}
            <div class="stat"><span>Placas / radiografías</span><strong>{placas.length}</strong></div>
          {/if}
          <div class="stat wide">
            <span>Última evolución</span>
            <strong>{notasClinicas[0]?.registradaEn ? new Date(notasClinicas[0].registradaEn!).toLocaleString('es-GT') : '—'}</strong>
          </div>
        </div>
        {#if notasClinicas[0]}
          <div class="preview-nota">
            <small>{notasClinicas[0].autor} · {notasClinicas[0].registradaEn ? new Date(notasClinicas[0].registradaEn).toLocaleString('es-GT') : ''}</small>
            <p>{notasClinicas[0].contenido}</p>
          </div>
        {/if}
      {:else if subTab === 'ficha'}
        <ConsultaIaPanel onAplicar={aplicarBorradorIa} />
        <form class="ficha-form" onsubmit={guardarFicha}>
          <p class="hint">Antecedentes y motivo de consulta. Use el micrófono para dictar en cada campo.</p>
          <div class="ficha-cols">
            <label class="field"><span>APP — Antecedentes personales</span>
              <div class="dict-row">
                <textarea rows="3" bind:value={fichaEdit.antecedentesPersonales} placeholder="Enfermedades crónicas…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'antecedentesPersonales'} onclick={() => toggleDictadoFicha('antecedentesPersonales')}>{dictandoCampo === 'antecedentesPersonales' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
            <label class="field"><span>APF — Antecedentes familiares</span>
              <div class="dict-row">
                <textarea rows="3" bind:value={fichaEdit.antecedentesFamiliares} placeholder="Enfermedades hereditarias…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'antecedentesFamiliares'} onclick={() => toggleDictadoFicha('antecedentesFamiliares')}>{dictandoCampo === 'antecedentesFamiliares' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
          </div>
          <div class="ficha-cols">
            <label class="field"><span>Hábitos tóxicos</span>
              <div class="dict-row">
                <textarea rows="2" bind:value={fichaEdit.habitosToxicos} placeholder="Tabaco, alcohol, drogas…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'habitosToxicos'} onclick={() => toggleDictadoFicha('habitosToxicos')}>{dictandoCampo === 'habitosToxicos' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
            <label class="field"><span>Alergias</span>
              <div class="dict-row">
                <textarea rows="2" bind:value={fichaEdit.alergias} placeholder="Medicamentos, alimentos…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'alergias'} onclick={() => toggleDictadoFicha('alergias')}>{dictandoCampo === 'alergias' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
          </div>
          <label class="field full"><span>Antecedentes quirúrgicos</span>
            <div class="dict-row">
              <textarea rows="2" bind:value={fichaEdit.antecedentesQuirurgicos} placeholder="Cirugías previas…"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'antecedentesQuirurgicos'} onclick={() => toggleDictadoFicha('antecedentesQuirurgicos')}>{dictandoCampo === 'antecedentesQuirurgicos' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <label class="field full"><span>Motivo de consulta</span>
            <div class="dict-row">
              <textarea rows="2" bind:value={fichaEdit.motivoConsulta} placeholder="Por qué acude hoy"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'motivoConsulta'} onclick={() => toggleDictadoFicha('motivoConsulta')}>{dictandoCampo === 'motivoConsulta' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <label class="field full"><span>HEA — Historia de la enfermedad actual</span>
            <div class="dict-row">
              <textarea rows="4" bind:value={fichaEdit.historiaEnfermedadActual} placeholder="Evolución del cuadro actual…"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'historiaEnfermedadActual'} onclick={() => toggleDictadoFicha('historiaEnfermedadActual')}>{dictandoCampo === 'historiaEnfermedadActual' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <label class="field full"><span>Diagnóstico</span>
            <div class="dict-row">
              <textarea rows="2" bind:value={fichaEdit.diagnostico} placeholder="Diagnóstico presuntivo o definitivo"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'diagnostico'} onclick={() => toggleDictadoFicha('diagnostico')}>{dictandoCampo === 'diagnostico' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <div class="ficha-actions">
            <button type="submit" class="primary" disabled={guardandoFicha}>{guardandoFicha ? 'Guardando…' : 'Guardar ficha clínica'}</button>
          </div>
        </form>
      {:else if subTab === 'evolucion'}
        <form class="form-inline" onsubmit={agregarNota}>
          <input placeholder={esFisio ? 'Fisioterapista (opcional)' : 'Autor (opcional)'} bind:value={notaNueva.autor} />
          <textarea rows="3" placeholder={esFisio ? NOTA_FISIO_PLACEHOLDER : 'Nota de evolución clínica…'} bind:value={notaNueva.contenido} required></textarea>
          <button type="submit" class="primary">Agregar nota</button>
        </form>
        <ul class="timeline">
          {#each notasClinicas as n (n.id)}
            <li>
              <div class="meta"><strong>{n.autor}</strong><time>{n.registradaEn ? new Date(n.registradaEn).toLocaleString('es-GT') : ''}</time></div>
              <p>{n.contenido}</p>
            </li>
          {:else}
            <li class="empty">Sin notas en este expediente.</li>
          {/each}
        </ul>
      {:else if subTab === 'signos'}
        {#if esFisio && paciente?.id}
          <FisioterapiaEvaluacionesPanel
            pacienteId={paciente.id}
            autor={fisioterapistaActual}
            {notas}
            onSaved={async () => { if (paciente) await cargarExpediente(paciente); }}
          />
        {:else}
        <form class="form-signos" onsubmit={registrarSigno}>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.ta : 'TA'} <input bind:value={signoNuevo.ta} placeholder={esFisio ? '7/10' : '120/80'} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.fc : 'FC'} <input type="number" bind:value={signoNuevo.fc} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.fr : 'FR'} <input type="number" bind:value={signoNuevo.fr} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.temp : 'Temp °C'} <input type="number" step="0.1" bind:value={signoNuevo.temp} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.satO2 : 'Sat O₂ %'} <input type="number" bind:value={signoNuevo.satO2} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.peso : 'Peso kg'} <input type="number" step="0.1" bind:value={signoNuevo.peso} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.talla : 'Talla m'} <input type="number" step="0.01" bind:value={signoNuevo.talla} /></label>
          <button type="submit" class="primary">Registrar</button>
        </form>
        <table>
          <thead><tr><th>Fecha</th><th>{esFisio ? 'EVA' : 'TA'}</th><th>{esFisio ? 'ROM flex.' : 'FC'}</th><th>{esFisio ? 'ROM abd.' : 'FR'}</th><th>{esFisio ? 'MMT' : 'Temp'}</th><th>{esFisio ? 'Func.' : 'Sat'}</th><th>{esFisio ? 'Perim.' : 'Peso'}</th></tr></thead>
          <tbody>
            {#each signos as s (s.id)}
              <tr>
                <td>{s.registradoEn ? new Date(s.registradoEn).toLocaleString('es-GT') : '—'}</td>
                <td>{s.ta ?? '—'}</td>
                <td>{s.fc ?? '—'}</td>
                <td>{s.fr ?? '—'}</td>
                <td>{s.temp ?? '—'}</td>
                <td>{s.satO2 ?? '—'}</td>
                <td>{s.peso ?? '—'}</td>
              </tr>
            {:else}
              <tr><td colspan="7">Sin signos registrados.</td></tr>
            {/each}
          </tbody>
        </table>
        {/if}
      {:else if subTab === 'rutina' && esFisio}
        <p class="hint">
          Plan de ejercicios domiciliarios prescrito por el fisioterapista. Al guardar queda en el expediente;
          use <strong>Enviar por WhatsApp</strong> para compartirla con el paciente.
          {#if rutinaVersiones > 0}<span class="meta"> · {rutinaVersiones} versión(es) archivada(s)</span>{/if}
        </p>
        <form class="rutina-form" onsubmit={guardarRutina}>
          <label class="wide">Título del plan
            <input bind:value={rutinaEdit.titulo} placeholder="Ej. Plan domiciliar — semana 2" required />
          </label>
          <label class="wide">Indicaciones generales
            <textarea rows="2" bind:value={rutinaEdit.indicaciones} placeholder="Precauciones, frecuencia, cuándo suspender…"></textarea>
          </label>

          <div class="rutina-toolbar">
            <span class="toolbar-label">Plantillas rápidas</span>
            {#each PLANTILLAS_EJERCICIO_FISIO as p, i (i)}
              <button type="button" class="chip" onclick={() => agregarPlantillaRutina(p)}>+ {p.nombre}</button>
            {/each}
            <button type="button" class="chip" onclick={agregarEjercicioRutina}>+ Fila vacía</button>
          </div>

          <div class="rutina-table-wrap">
            <table class="rutina-table">
              <thead>
                <tr>
                  <th>Ejercicio</th><th>Series</th><th>Reps</th><th>Hold</th><th>Frecuencia</th><th>Progresión</th><th></th>
                </tr>
              </thead>
              <tbody>
                {#each rutinaEdit.ejercicios as ej, idx (idx)}
                  <tr>
                    <td><input bind:value={ej.nombre} placeholder="Nombre del ejercicio" required /></td>
                    <td><input type="number" min="1" bind:value={ej.series} class="num" /></td>
                    <td><input bind:value={ej.reps} class="num" /></td>
                    <td><input bind:value={ej.hold} placeholder="30 s" /></td>
                    <td><input bind:value={ej.frecuencia} placeholder="2×/día" /></td>
                    <td><input bind:value={ej.progresion} placeholder="Subir reps…" /></td>
                    <td><button type="button" class="link danger" onclick={() => quitarEjercicioRutina(idx)} title="Quitar">×</button></td>
                  </tr>
                {:else}
                  <tr><td colspan="7" class="empty">Sin ejercicios. Use plantillas rápidas o agregue una fila.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>

          <div class="rutina-actions">
            <button type="submit" class="primary">Guardar rutina</button>
            <button type="button" class="wa" onclick={enviarRutinaWhatsApp} disabled={!paciente.telefono}>
              Enviar por WhatsApp
            </button>
          </div>
        </form>
      {:else if subTab === 'receta'}
        <p class="hint">
          Emite la receta desde la consulta. Se vincula al médico en sesión
          {#if medicoActual}(<strong>{medicoActual}</strong>){/if}
          y la farmacia la despacha en el POS.
        </p>
        <form class="form-receta" onsubmit={emitirReceta}>
          <label>Diagnóstico <input bind:value={recetaDiagnostico} placeholder="Diagnóstico clínico" /></label>
          <button type="button" onclick={agregarItemReceta}>+ Medicamento</button>
          {#if recetaItems.length > 0}
            <table>
              <thead><tr><th>Medicamento</th><th>Cant.</th><th>Indicaciones</th><th></th></tr></thead>
              <tbody>
                {#each recetaItems as it, i (i)}
                  <tr>
                    <td><input bind:value={it.descripcion} required placeholder="Nombre / dosis" /></td>
                    <td><input type="number" min="1" bind:value={it.cantidad} class="num" /></td>
                    <td><input bind:value={it.indicaciones} placeholder="Posología" /></td>
                    <td><button type="button" onclick={() => quitarItemReceta(i)}>×</button></td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
          <button type="submit" class="primary" disabled={recetaItems.length === 0}>Emitir receta</button>
        </form>
        <h4>Recetas del paciente</h4>
        <table>
          <thead><tr><th>Fecha</th><th>Diagnóstico</th><th>Estado</th><th></th></tr></thead>
          <tbody>
            {#each recetasPaciente as r (r.id)}
              <tr>
                <td>{r.fecha?.slice(0, 10) ?? '—'}</td>
                <td>{r.diagnostico ?? '—'}</td>
                <td>{r.estado}</td>
                <td>
                  {#if r.estado !== 'ANULADA'}
                    <button type="button" onclick={() => verReceta(r)}>Vista previa</button>
  <button type="button" onclick={() => imprimirReceta(r)}>Imprimir ℞</button>
                  {/if}
                </td>
              </tr>
            {:else}
              <tr><td colspan="4">Sin recetas en este expediente.</td></tr>
            {/each}
          </tbody>
        </table>
      {:else if subTab === 'odontograma' && esDental && paciente?.id}
        <OdontogramaPanel pacienteId={paciente.id} />
      {:else if subTab === 'placas' && esDental}
        <p class="hint">
          La mayoría de consultorios odontológicos toman radiografías intraorales o panorámicas en sitio.
          Las placas quedan vinculadas al expediente del paciente (CUI).
        </p>
        <form class="form-placa" onsubmit={guardarPlaca}>
          <label>Tipo
            <select bind:value={placaNueva.tipo}>
              {#each TIPOS_PLACA as t}<option value={t.value}>{t.label}</option>{/each}
            </select>
          </label>
          <label>Diente(s) / región <input bind:value={placaNueva.dientes} placeholder="Ej. 18, 17-16 o cuadrante" required /></label>
          <label>Odontólogo <input bind:value={placaNueva.medico} placeholder="Dr(a). …" /></label>
          <label>Observaciones <input bind:value={placaNueva.nota} placeholder="Hallazgo, indicación…" /></label>
          <label class="file">
            Imagen / placa
            <input type="file" accept="image/*,.dcm" onchange={onArchivoPlaca} />
          </label>
          <button type="submit" class="primary">Archivar placa</button>
        </form>

        <div class="placas-grid">
          {#each placas as p (p.id)}
            <article class="placa-card">
              {#if p.imagenUrl}
                <img src={p.imagenUrl} alt={`Placa ${p.dientes}`} />
              {:else}
                <div class="placa-placeholder">🩻 {p.tipo}</div>
              {/if}
              <div class="placa-meta">
                <strong>{p.dientes}</strong>
                <span>{p.tipo} · {p.fecha}</span>
                <span>{p.medico}</span>
                {#if p.nota}<p>{p.nota}</p>{/if}
                {#if p.archivoNombre}<small>{p.archivoNombre}</small>{/if}
                <button type="button" class="link danger" onclick={() => eliminarPlaca(p.id)}>Eliminar</button>
              </div>
            </article>
          {:else}
            <p class="hint">Sin placas archivadas. Use el formulario para registrar la primera radiografía.</p>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .page { display: flex; flex-direction: column; gap: 0.85rem; }
  .page-header { display: flex; align-items: center; gap: 1rem; padding: 1.4rem 1.6rem;
    background: linear-gradient(135deg, #0d9488, #0f766e, #0c4a6e); background-size: 200% 200%;
    animation: hShift 8s ease infinite; border-radius: var(--radius-xl); color: #fff; }
  .header-icon { font-size: 2rem; width: 52px; height: 52px; display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.15); border-radius: 14px; flex-shrink: 0; }
  .header-info h1 { margin: 0; font-size: 1.3rem; font-weight: 800; color: #fff !important; }
  .header-info p { margin: 0.2rem 0 0; font-size: 0.8rem; color: rgba(255,255,255,0.7); }
  @keyframes hShift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }

  .hint { color: var(--text-secondary); font-size: .85rem; margin: 0 0 .5rem; }
  .meta { color: var(--text-muted); font-size: .82rem; }

  .demo-banner { display: flex; flex-direction: column; gap: .25rem; padding: .65rem .85rem; margin-bottom: .5rem;
    border-radius: .75rem; border: 1px solid rgba(13,148,136,0.2); background: rgba(13,148,136,0.06);
    font-size: .85rem; color: var(--brand-deep); }
  .demo-banner strong { font-size: .75rem; text-transform: uppercase; letter-spacing: .04em; }
  .cui-link { border: 0; background: none; color: var(--brand); font-weight: 800; cursor: pointer; text-decoration: underline; padding: 0 .15rem; }
  .demo-more { font-size: .78rem; color: var(--brand,#0d9488); }

  .cui-bar { display: flex; gap: .6rem; flex-wrap: wrap; align-items: end; margin-bottom: .75rem; }
  .cui-form { display: flex; gap: .6rem; flex-wrap: wrap; align-items: end; flex: 1; min-width: 220px; }
  .cui-field { display: flex; flex-direction: column; gap: .2rem; flex: 1; min-width: 220px; }
  .picker-field { flex: 1.2; min-width: 260px; }
  .cui-field span { font-size: .72rem; font-weight: 700; color: var(--brand-deep); text-transform: uppercase; letter-spacing: .04em; }

  input, select, textarea { border-radius: .55rem; border: 1px solid var(--glass-border); padding: .48rem .6rem; font: inherit; font-size: .85rem; background: rgba(255,255,255,0.7); color: var(--text-primary); }
  input:focus, select:focus, textarea:focus { outline: none; border-color: var(--brand); box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand,#0d9488) 15%, transparent); }
  button { cursor: pointer; font: inherit; border-radius: .55rem; }
  button.primary { background: var(--brand,#0d9488); color: #fff; border: 0; font-weight: 700; padding: .5rem 1rem; box-shadow: 0 4px 14px color-mix(in srgb, var(--brand,#0d9488) 25%, transparent); transition: all .2s; }
  button.primary:hover:not(:disabled) { background: var(--brand-deep); transform: translateY(-1px); }
  .readonly { background: rgba(0,0,0,0.03); color: var(--text-secondary); }

  .msg { padding: .55rem .85rem; border-radius: .65rem; font-size: .82rem; font-weight: 600; }
  .msg-error { background: rgba(239,68,68,0.08); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
  .msg-ok { background: rgba(16,185,129,0.08); color: #059669; border: 1px solid rgba(16,185,129,0.2); }

  .alta-card, .paciente-card { border: 1px solid var(--glass-border); border-radius: var(--radius-xl); padding: 1.1rem;
    background: var(--glass-bg-card); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur);
    box-shadow: var(--glass-shadow); margin-top: .65rem; }

  .form-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: .55rem; }
  .form-grid label { display: flex; flex-direction: column; gap: .2rem; font-size: .82rem; }

  .paciente-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: .5rem; margin-bottom: .65rem; padding-bottom: .65rem; border-bottom: 1px solid var(--glass-border); }
  .head-actions { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
  .print-btn { background: #fff; color: #15803d; border: 1px solid #16a34a; font-weight: 600; padding: .45rem .8rem; cursor: pointer; border-radius: 8px; font: inherit; }
  .print-btn:hover { background: #f0fdf4; }
  .cui-badge { display: inline-block; margin-left: .4rem; padding: .15rem .5rem; background: color-mix(in srgb, var(--brand,#0d9488) 10%, transparent); color: var(--brand-deep); border-radius: 999px; font-size: .75rem; font-weight: 700; }

  .subtabs { display: flex; gap: .3rem; flex-wrap: wrap; margin-bottom: .85rem; }
  .subtabs button { border: 1px solid var(--glass-border); background: var(--glass-bg-card); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); padding: .4rem .75rem; border-radius: 999px; font-size: .82rem; font-weight: 600; color: var(--text-secondary); transition: all .2s; }
  .subtabs button:hover { border-color: var(--brand); color: var(--brand-deep); }
  .subtabs button.active { background: var(--brand,#0d9488); color: #fff; border-color: var(--brand,#0d9488); box-shadow: 0 4px 12px color-mix(in srgb, var(--brand,#0d9488) 30%, transparent); }

  .resumen-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(135px, 1fr)); gap: .55rem; margin-bottom: .85rem; }
  .stat { background: var(--glass-bg-card); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); border: 1px solid var(--glass-border); border-radius: .75rem; padding: .6rem .7rem; }
  .stat span { display: block; font-size: .7rem; color: var(--text-secondary); font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
  .stat strong { font-size: 1.3rem; color: var(--text-primary); font-weight: 800; }
  .stat.wide { grid-column: 1 / -1; }

  .preview-nota { background: rgba(255,255,255,0.5); border-left: 4px solid var(--brand,#0d9488); padding: .6rem .8rem; border-radius: 0 .6rem .6rem 0; }
  .preview-nota p { margin: .3rem 0 0; white-space: pre-wrap; font-size: .85rem; }

  .form-inline { display: flex; flex-direction: column; gap: .45rem; margin-bottom: .85rem; }
  .timeline { list-style: none; padding: 0; margin: 0; }
  .timeline li { border: 1px solid var(--glass-border); border-radius: .75rem; padding: .7rem; margin-bottom: .45rem;
    background: var(--glass-bg-card); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); transition: box-shadow .2s; }
  .timeline li:hover { box-shadow: var(--glass-shadow); }
  .timeline .meta { display: flex; justify-content: space-between; font-size: .8rem; margin-bottom: .2rem; }
  .timeline p { margin: 0; white-space: pre-wrap; }
  .empty { color: var(--text-muted); font-style: italic; }

  .form-signos { display: flex; flex-wrap: wrap; gap: .45rem; align-items: end; margin-bottom: .85rem; }
  .form-signos label { display: flex; flex-direction: column; gap: .15rem; font-size: .78rem; }
  .form-signos input { width: 5.5rem; }
  .form-receta { display: flex; flex-direction: column; gap: .55rem; margin-bottom: .85rem; }
  .form-receta label { display: flex; flex-direction: column; gap: .2rem; font-size: .82rem; }
  .form-receta .num { width: 4rem; }

  table { width: 100%; border-collapse: collapse; font-size: .84rem; }
  th, td { border-bottom: 1px solid var(--glass-border); padding: .4rem .5rem; text-align: left; }
  th { font-size: .66rem; text-transform: uppercase; letter-spacing: .04em; color: var(--text-secondary); font-weight: 700; background: color-mix(in srgb, var(--brand,#0d9488) 4%, transparent); }
  tr:hover td { background: rgba(255,255,255,0.35); }

  .form-placa { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: .55rem; margin-bottom: .85rem; }
  .form-placa label { display: flex; flex-direction: column; gap: .2rem; font-size: .82rem; }
  .file input[type="file"] { padding: .3rem; }

  .placas-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: .65rem; }
  .placa-card { border: 1px solid var(--glass-border); border-radius: .85rem; overflow: hidden;
    background: var(--glass-bg-card); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); transition: box-shadow .2s; }
  .placa-card:hover { box-shadow: var(--glass-shadow); }
  .placa-card img { width: 100%; height: 130px; object-fit: cover; background: #111; }
  .placa-placeholder { height: 130px; display: grid; place-items: center; background: #1e293b; color: #e2e8f0; font-size: 1rem; }
  .placa-meta { padding: .5rem .6rem; font-size: .8rem; display: flex; flex-direction: column; gap: .12rem; }
  .placa-meta strong { color: var(--text-primary); }
  .placa-meta p { margin: .2rem 0 0; }

  .link { background: none; border: 0; color: var(--brand); padding: .2rem 0; align-self: flex-start; font-weight: 600; }
  .link.danger { color: #dc2626; }

  .rutina-form { display: flex; flex-direction: column; gap: .65rem; }
  .rutina-form .wide { display: flex; flex-direction: column; gap: .2rem; font-size: .82rem; }
  .rutina-toolbar { display: flex; flex-wrap: wrap; gap: .3rem; align-items: center; }
  .toolbar-label { font-size: .75rem; font-weight: 700; color: var(--text-secondary); margin-right: .2rem; }
  .chip { font-size: .75rem; padding: .25rem .55rem; border-radius: 999px; border: 1px solid var(--glass-border);
    background: var(--glass-bg-card); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); cursor: pointer;
    color: var(--brand-deep); font-weight: 600; transition: all .15s; }
  .chip:hover { background: var(--brand-surface); border-color: var(--brand); }
  .rutina-table-wrap { overflow-x: auto; }
  .rutina-table { width: 100%; border-collapse: collapse; font-size: .82rem; }
  .rutina-table th, .rutina-table td { padding: .3rem; border-bottom: 1px solid var(--glass-border); vertical-align: middle; }
  .rutina-table input { width: 100%; min-width: 3rem; padding: .35rem .4rem; font-size: .8rem; }
  .rutina-table .num { max-width: 3.5rem; }
  .rutina-actions { display: flex; flex-wrap: wrap; gap: .45rem; margin-top: .2rem; }
  button.wa { background: #25d366; color: #fff; border: 0; padding: .5rem .85rem; border-radius: .55rem; font-weight: 700; }
  button.wa:disabled { opacity: .45; cursor: not-allowed; }

  button.mic { background: #fff; color: #0f766e; border: 1px solid #14b8a6; padding: .45rem .7rem; border-radius: 8px; font-weight: 700; cursor: pointer; }
  button.mic.on { background: #dc2626; color: #fff; border-color: #dc2626; }
  .ficha-form { display: flex; flex-direction: column; gap: .6rem; }
  .ficha-form .field { display: flex; flex-direction: column; gap: .3rem; }
  .ficha-form .field span { font-size: .72rem; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: .03em; }
  .ficha-form .full { width: 100%; }
  .ficha-form textarea { width: 100%; box-sizing: border-box; resize: vertical; }
  .ficha-actions { margin-top: .35rem; }
  .dict-row { display: flex; gap: .4rem; align-items: flex-start; }
  .dict-row textarea { flex: 1; }
  .dict-row .mic { flex-shrink: 0; margin-top: .2rem; }
  .ficha-cols { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; }
  @media (max-width: 620px) { .ficha-cols { grid-template-columns: 1fr; } }
</style>
